import { type AppRole, AppRoleSchema, RevokeSessionsBodySchema } from '@gestion-permisos/contracts';
import { type FormEvent, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { z } from 'zod';
import { ApiProblem } from '../api/problem.js';
import { describedBy, Field, firstForbidden, INPUT, isStale, Notice } from '../admin/admin-ui.js';
import { EmployeeForm } from '../admin/employee-form.js';
import { useAdminEmployee, useAssignRole, useEndRole, useRevokeSessions, useRoles, useUpdateEmployee } from '../admin/people-api.js';
import { ApiErrorView } from '../components/api-error-view.js';
import { buttonClasses } from '../components/button-styles.js';
import { ChoiceList } from '../components/choice-list.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/card.js';
import { PageHeader } from '../components/page-header.js';
import { ProblemMessage } from '../components/problem-message.js';
import { formatInstant } from '../requests/format.js';
import { TABLE, TD, TH } from '../requests/request-list.js';

const NOT_A_PERSON = new ApiProblem(404, 'No encontrada', 'NOT_FOUND', undefined, undefined, []);

export const ROLE_LABELS: Record<AppRole, string> = { ADMINISTRADOR: 'Administrador', RESPONSABLE: 'Responsable', EMPLEADO: 'Empleado' };

/** Botones de esta pantalla, con el aspecto de las ya adaptadas (`admin-ui.tsx` conserva los suyos). */
const BUTTON = buttonClasses();

/**
 * Detalle de un empleado (W8): edición (`PATCH` con `If-Match`; un 412 vuelve a leer `GET /employees/{id}`),
 * roles (asignar y retirar con `Idempotency-Key`) y revocación de sesiones. Un id que no es UUID da «No
 * encontrada» sin llamar a la API; un 403 se muestra una vez. Con el aspecto de las pantallas ya adaptadas:
 * cabecera y tres tarjetas (Datos, Roles y Sesiones); el formulario compartido conserva sus clases.
 */
export function AdminEmployeePage() {
  const { id = '' } = useParams();
  if (!z.uuid().safeParse(id).success) return <ApiErrorView error={NOT_A_PERSON} />;
  return <AdminEmployee id={id} />;
}

function AdminEmployee({ id }: { id: string }) {
  const employee = useAdminEmployee(id);
  const update = useUpdateEmployee(id);
  const roles = useRoles(id);
  const assign = useAssignRole(id);
  const end = useEndRole(id);
  const revoke = useRevokeSessions(id);
  const [saved, setSaved] = useState<string | undefined>(undefined);

  const denied = firstForbidden(employee.error, update.error, roles.error, assign.error, end.error, revoke.error);
  if (denied) return <ApiErrorView error={denied} />;
  if (employee.isPending) return <output className="block text-muted-foreground">Cargando…</output>;
  if (employee.isError) return <ApiErrorView error={employee.error} />;
  const person = employee.data;

  return (
    <div className="space-y-7 lg:px-6 lg:py-4">
      <Link to="/administracion/empleados" className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground">
        Volver a empleados
      </Link>
      <PageHeader title={person.fullName} description="Datos, roles y revocación de sesiones." />

      <Card>
        <CardHeader>
          <CardTitle>Datos</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {isStale(update.error) ? <Notice text="Otra persona cambió el empleado. Se cargaron los datos actuales: revísalos y vuelve a guardar." /> : null}
          <EmployeeForm
            // Una versión nueva (p. ej., tras un 412) recarga el formulario.
            key={person.version}
            employee={person}
            pending={update.isPending}
            error={update.error}
            notice={saved}
            submitLabel="Guardar cambios"
            onUpdate={(changes) => {
              setSaved(undefined);
              update.mutate({ version: person.version, changes }, { onSuccess: () => setSaved('Cambios guardados.') });
            }}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Roles</CardTitle>
        </CardHeader>
        <CardContent>
          <RolesSection roles={roles} assign={assign} end={end} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Sesiones</CardTitle>
        </CardHeader>
        <CardContent>
          <RevokeSessionsForm revoke={revoke} />
        </CardContent>
      </Card>
    </div>
  );
}

function RolesSection({
  roles,
  assign,
  end,
}: {
  roles: ReturnType<typeof useRoles>;
  assign: ReturnType<typeof useAssignRole>;
  end: ReturnType<typeof useEndRole>;
}) {
  const [role, setRole] = useState<AppRole>('EMPLEADO');
  const error = assign.error ?? end.error;
  const pending = assign.isPending || end.isPending;
  return (
    <div className="space-y-3">
      {roles.isPending ? <output className="block text-muted-foreground">Cargando…</output> : null}
      {roles.isError ? <ProblemMessage error={roles.error} /> : null}
      {/* 409 (p. ej., LAST_ADMINISTRATOR) o 412: tal como llegan; los roles se vuelven a leer. */}
      {error !== null ? <ProblemMessage error={error} /> : null}
      {roles.isSuccess && roles.data.length === 0 ? <p className="text-muted-foreground">Sin roles asignados.</p> : null}
      {roles.isSuccess && roles.data.length > 0 ? (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className={TABLE} aria-label="Roles">
            <thead className="border-b border-border bg-muted text-muted-foreground">
              <tr>
                <th scope="col" className={TH}>Rol</th>
                <th scope="col" className={TH}>Estado</th>
                <th scope="col" className={TH}>Asignado</th>
                <th scope="col" className={TH}>Retirado</th>
                <th scope="col" className={TH}>
                  <span className="sr-only">Acciones</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {roles.data.map((assignment) => (
                <tr key={assignment.id} className="border-t border-border first:border-t-0">
                  <th scope="row" className={TH}>{ROLE_LABELS[assignment.role]}</th>
                  <td className={TD}>{assignment.active ? 'Vigente' : 'Retirado'}</td>
                  <td className={TD}>{formatInstant(assignment.assignedAt)}</td>
                  <td className={TD}>{assignment.endedAt ? formatInstant(assignment.endedAt) : '—'}</td>
                  <td className={TD}>
                    {assignment.active ? (
                      <button
                        type="button"
                        disabled={pending}
                        className={BUTTON}
                        aria-label={`Retirar ${ROLE_LABELS[assignment.role]}`}
                        onClick={() => {
                          assign.reset();
                          end.mutate({ role: assignment.role, version: assignment.version });
                        }}
                      >
                        Retirar
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          end.reset();
          assign.mutate(role);
        }}
      >
        <Field id="role-assign" label="Rol">
          <ChoiceList
            id="role-assign"
            value={role}
            options={AppRoleSchema.options.map((option) => ({ value: option, label: ROLE_LABELS[option] }))}
            onChange={(value) => setRole(AppRoleSchema.parse(value))}
            className={INPUT}
          />
        </Field>
        <button type="submit" disabled={pending} className={BUTTON}>
          Asignar rol
        </button>
      </form>
    </div>
  );
}

/** Revocar las sesiones: el motivo es obligatorio (3 a 500) y se valida antes de enviar. No se escribe en ningún log. */
function RevokeSessionsForm({ revoke }: { revoke: ReturnType<typeof useRevokeSessions> }) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [notice, setNotice] = useState<string | undefined>(undefined);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setNotice(undefined);
    const parsed = RevokeSessionsBodySchema.safeParse({ reason });
    if (!parsed.success) {
      setError('Escribe el motivo (de 3 a 500 caracteres).');
      return;
    }
    setError(undefined);
    revoke.mutate(parsed.data, {
      onSuccess: () => {
        setReason('');
        // Confirmación sin repetir el motivo.
        setNotice('Se revocaron las sesiones del empleado.');
      },
    });
  };

  return (
    <form noValidate onSubmit={submit} className="max-w-xl space-y-3" aria-label="Revocar sesiones">
      <Notice text={notice} />
      {revoke.error ? <ProblemMessage error={revoke.error} /> : null}
      <Field id="revoke-reason" label="Motivo de la revocación" error={error}>
        <textarea
          id="revoke-reason"
          rows={2}
          maxLength={500}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          className={INPUT}
          {...describedBy('revoke-reason', error)}
        />
      </Field>
      <button type="submit" disabled={revoke.isPending} className={BUTTON}>
        Revocar sesiones
      </button>
    </form>
  );
}
